import { useCallback, useEffect, useMemo, useRef } from 'preact/hooks';
import useAppDispatch from '../../../hooks/useAppDispatch';
import useAppSelector from '../../../hooks/useAppSelector';
import { useTranslation } from '../../../hooks/useTranslation';
import { ProductGroup } from '../../../models/ProductGroup';
import { isProductSearchGroupsFeatureItem } from '../../../services/FeatureService';
import FiltersBar from '../../filters/FiltersBar';
import Loader from '../../loader/Loader';
import ProductGroupList from '../../product/grid/ProductGroupList';
import MainFeatureError from '../common/MainFeatureError';
import MainFeatureHeader from '../common/MainFeatureHeader';
import './ProductSearchGroupsResult.less';
import EmptyProductSearchResult from './EmptyProductSearchResult';
import ProductSuggestionsPanel from '../common/ProductSuggestionsPanel';
import type { LoadingStatus } from '../../../models/LoadingStatus';
import { transformCombinationsToProductGroups } from '../../../services/ProductGroupService';
import Chip from '../../chip/Chip';

const ProductSearchGroupsResult = () => {
    const highlightedProductId = useAppSelector(
        state => state.productsGridState.highlightedProductId
    );
    const mainFeatureItem = useAppSelector(
        state => state.featureState.mainFeatureItem
    );
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const gridRef = useRef<HTMLDivElement>(null);

    const loadingStatus: LoadingStatus = useMemo(() => {
        if (!isProductSearchGroupsFeatureItem(mainFeatureItem)) {
            return 'idle';
        }
        const { loadingStatus, refinementLoadingStatus } = mainFeatureItem;
        if (
            loadingStatus === 'loading' ||
            refinementLoadingStatus === 'loading'
        ) {
            return 'loading';
        }
        if (loadingStatus === 'error' || refinementLoadingStatus === 'error') {
            return 'error';
        }
        if (
            loadingStatus === 'success' ||
            refinementLoadingStatus === 'success'
        ) {
            return 'success';
        }
        return 'idle';
    }, [mainFeatureItem]);

    const productGroups: ProductGroup[] = useMemo(() => {
        if (!isProductSearchGroupsFeatureItem(mainFeatureItem)) {
            return [];
        }
        if (mainFeatureItem.featureType === 'product-search-combinations') {
            return transformCombinationsToProductGroups(
                mainFeatureItem.groups,
                {
                    withLink: true,
                    baseProductsIds: mainFeatureItem.baseProductsIds
                }
            );
        } else {
            return transformCombinationsToProductGroups(
                mainFeatureItem.groups,
                { withLink: false }
            );
        }
    }, [mainFeatureItem]);

    const suggestedGroups: ProductGroup[] = useMemo(() => {
        if (!isProductSearchGroupsFeatureItem(mainFeatureItem)) {
            return [];
        }
        return [
            {
                title: t('PRODUCTS_SUGGESTED_TITLE'),
                subtitle: t('PRODUCTS_SUGGESTED_SUBTITLE'),
                products: mainFeatureItem.suggestedProducts
            }
        ];
    }, [mainFeatureItem]);

    const getLoadingTitles = useCallback((): {
        title?: string;
        subtitle?: string;
    } => {
        if (!isProductSearchGroupsFeatureItem(mainFeatureItem)) {
            return {};
        }
        switch (mainFeatureItem.featureType) {
            // For now, both titles are the same, but they can be customized later.
            case 'product-search-combinations-by-image':
            case 'product-search-combinations':
                return {
                    title: t('PRODUCT_SEARCH_COMBINATIONS_LOADING_TITLE'),
                    subtitle: t('PRODUCT_SEARCH_COMBINATIONS_LOADING_SUBTITLE')
                };
            default:
                return {};
        }
    }, [mainFeatureItem]);

    const scrollToProduct = useCallback(
        (productId: string) => {
            const items = gridRef.current?.querySelectorAll(
                `[data-bri-product-id]`
            );
            if (items) {
                items.forEach(item => item.classList.remove('bri-highlighted'));
            }
            const item = gridRef.current?.querySelector(
                `[data-bri-product-id="${productId}"]`
            );
            item?.scrollIntoView({ behavior: 'smooth' });
            item?.classList.add('bri-highlighted');
        },
        [gridRef.current]
    );

    useEffect(() => {
        if (!highlightedProductId) {
            return;
        }
        scrollToProduct(highlightedProductId);
        const timeout = setTimeout(() => {
            dispatch.productsGridState.setHighlightedProductId(undefined);
        }, 100);
        return () => clearTimeout(timeout);
    }, [highlightedProductId, scrollToProduct]);

    if (!isProductSearchGroupsFeatureItem(mainFeatureItem)) {
        return null;
    }

    const getMainFeatureHeader = () => {
        if (
            !isProductSearchGroupsFeatureItem(mainFeatureItem) ||
            mainFeatureItem.loadingStatus === 'loading'
        ) {
            return null;
        }
        switch (mainFeatureItem.featureType) {
            case 'product-search-combinations':
                return (
                    <MainFeatureHeader
                        preTitle={t(
                            'PRODUCT_SEARCH_COMBINATIONS_RESULTS_PRETITLE'
                        )}
                        title={
                            mainFeatureItem.baseProducts.length === 1
                                ? mainFeatureItem.baseProducts[0].name
                                : t('PRODUCTS_LABEL_MANY', {
                                      count: mainFeatureItem.baseProducts.length
                                  })
                        }
                        products={mainFeatureItem.baseProducts}
                        visibleActions={[
                            'add-to-cart',
                            'product-search-similar',
                            'compare',
                            'product-mix-and-match'
                        ]}
                    />
                );
            case 'product-search-combinations-by-image':
                return (
                    <MainFeatureHeader
                        preTitle={
                            <>
                                {t(
                                    'PRODUCT_SEARCH_COMBINATIONS_RESULTS_PRETITLE'
                                )}
                                {mainFeatureItem.selectedBoundingBox && (
                                    <>
                                        {': '}
                                        <Chip variant="primary-50">
                                            {mainFeatureItem.selectedBoundingBox
                                                ?.categoryTranslatedValue ??
                                                mainFeatureItem
                                                    .selectedBoundingBox
                                                    ?.categoryValue}
                                        </Chip>
                                    </>
                                )}
                            </>
                        }
                        title={t(
                            'PRODUCT_SEARCH_COMBINATIONS_BY_IMAGE_RESULTS_TITLE'
                        )}
                        imageUrl={
                            mainFeatureItem.imageDataUrl ??
                            mainFeatureItem.imageUrl
                        }
                        imageWidth={mainFeatureItem.imageWidth}
                        imageHeight={mainFeatureItem.imageHeight}
                        boundingBoxes={mainFeatureItem.boundingBoxes}
                        selectedBoundingBox={
                            mainFeatureItem.selectedBoundingBox
                        }
                    />
                );
        }
    };

    const isEmptyResult =
        mainFeatureItem.groups.length === 0 ||
        mainFeatureItem.groups.every(value => value.products.length === 0);

    const isInitialSearch =
        mainFeatureItem.appliedFieldFilters.length === 0 &&
        mainFeatureItem.appliedCategoryFilters.length === 0 &&
        mainFeatureItem.appliedMinPrice === null &&
        mainFeatureItem.appliedMaxPrice === null;

    return (
        <div className={`bri-ProductSearchGroupsResult`}>
            <div
                ref={gridRef}
                className={`bri-ProductSearchGroupsResult-body bri-SlimScroll`}
            >
                {getMainFeatureHeader()}
                {mainFeatureItem.loadingStatus === 'success' &&
                    !(isInitialSearch && isEmptyResult) && <FiltersBar />}
                {loadingStatus === 'success' && !isEmptyResult && (
                    <ProductGroupList
                        productGroups={productGroups}
                        source="brilliant"
                        featureType={mainFeatureItem.featureType}
                    />
                )}
                {loadingStatus === 'success' && isEmptyResult && (
                    <EmptyProductSearchResult />
                )}
                {loadingStatus === 'success' &&
                    suggestedGroups &&
                    suggestedGroups.length > 0 &&
                    suggestedGroups.some(g => g.products.length > 0) && (
                        <ProductSuggestionsPanel
                            suggestions={suggestedGroups.map(group => ({
                                title: group.title,
                                description: group.subtitle || '',
                                products: group.products
                            }))}
                            featureType={mainFeatureItem.featureType}
                        />
                    )}
                {loadingStatus === 'loading' && (
                    <Loader {...getLoadingTitles()} />
                )}
                {loadingStatus === 'error' && <MainFeatureError />}
            </div>
        </div>
    );
};

export default ProductSearchGroupsResult;
